import { useDispatch, useSelector } from "react-redux";
import { usersSelector } from "../../Reducers/usersReducer";
import styles from "./users-and-roles.module.css";
import useListProcessing from "../../Hooks/useListProcessing";
import { enterpriseDetailsSelector } from "../../Reducers/enterpriseDetailsReducer";
import { useEffect, useMemo, useState } from "react";
import UsersTable from "./UsersTable";
import { usersUpdateSelector, USERS_UPDATE_RESET } from "../../Reducers/usersUpdateReducer";
import PlusIcon from "../Icons/plus-icon";
import UserInvitationModal from "./UserInvitationModal";
import UserEditModal from "./UserEditModal";
import { useFetchRolesQuery } from "../../Apis/rolesApi";
import { RolesList } from "../../Model/userObjects";
import UserControls from "./UserControls";
import { useFetchPendingUserInvitationsQuery } from "../../Apis/userInvitationsApi";
import { UserInvitationsList } from "../../Model/userObjects";

const processConfigs = {
  filter: {
    TYPE: ({user}, value) => value === "ALL" ? true : user.getAdministratorLevel() === value,
    ROLE: ({user}, value) => value === "ALL" ? true : user.getRoleId() === value,
    STATUS: ({user, type}, value) => {
      switch (value) {
        case "ACTIVE":
          return type === "USER" && user.getIsActive();
        case "INACTIVE":
          return type === "USER" && !user.getIsActive();
        case "PENDING":
          return type === "INVITATION";
        default:
          return true;
      }
    },
    SEARCH: ({user}, value) => {
      return user.getName().toLowerCase().includes(value.toLowerCase()) || user.getEmail().toLowerCase().includes(value.toLowerCase());
    }
  }
}

const UsersSettings = ({setSuccessNotification, setErrorNotification}) => {
  const dispatch = useDispatch();
  const users = useSelector(usersSelector);
  const { data: roles = new RolesList() } = useFetchRolesQuery();
  const { data: invitations = new UserInvitationsList([]) } = useFetchPendingUserInvitationsQuery();
  const partnerDetails = useSelector(enterpriseDetailsSelector);
  const usersUpdate = useSelector(usersUpdateSelector);
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  
  const data = useMemo(() => {
    const list1 = users.getUsers().map(user => {
      return {
        type: "USER",
        user: user,
        isSelf: user.getId() === partnerDetails?.id,
        role: roles.getRoleById(user.getRoleId()),
      }
    })
    const list2 = invitations.getInvitations().map(invitation => {
      return {
        type: "INVITATION",
        user: invitation,
        isSelf: false,
        role: roles.getRoleById(invitation.getRoleId()),
      }
    })
    return [...list1, ...list2];
  }, [users, partnerDetails, invitations, roles]);

  useEffect(() => {
    if (usersUpdate.success) {
      dispatch({ type: USERS_UPDATE_RESET });
      setSuccessNotification("User updated successfully");
    }
  }, [usersUpdate, dispatch, setSuccessNotification]);

  const handleInvitationsSent = () => {
    setInviteModalOpen(false);
    setSuccessNotification("Invitations sent successfully");
  };

  const handleInvitationSendError = (errorMessage) => {
    setErrorNotification(errorMessage);
  };

  const handleInvitationDeleted = () => {
    setSuccessNotification("Invitation deleted successfully");
  };

  const handleInvitationDeleteError = () => {
    setErrorNotification("Failed to delete invitation");
  };

  const handleInvitationResent = () => {
    setSuccessNotification("Invitation resent successfully");
  };

  const handleInvitationResendError = (error) => {
    const errorMessage = error?.data?.errorMessage || "Failed to resend invitation";
    setErrorNotification(errorMessage);
  };

  const { processedData, setFilter, getFilter } = useListProcessing(data, processConfigs);

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setEditModalOpen(false);
    setSelectedUser(null);
  };

  return (
    <>
      <div className={styles.content}>
        <div className={styles.usersHeading}>
          <div className={styles.usersHeadingTitle}>Users</div>
          <div className={styles.usersHeadingDescription}>
            Manage users and their roles
          </div>
        </div>
        <div className={styles.usersControls}>
          <div className={styles.usersControlsLeft}>
            <UserControls getFilter={getFilter} setFilter={setFilter} />
          </div>
          <div className={styles.usersControlsRight}>
            <button
              className={styles.usersInviteButton}
              onClick={() => setInviteModalOpen(true)}
            >
              <PlusIcon />
              <div className={styles.usersInviteButtonText}>Invite user</div>
            </button>
          </div>
        </div>
        <UsersTable
          data={processedData}
          onEdit={handleEditUser}
          onInvitationDeleted={handleInvitationDeleted}
          onInvitationDeleteError={handleInvitationDeleteError}
          onInvitationResent={handleInvitationResent}
          onInvitationResendError={handleInvitationResendError}
        />
      </div>

      <UserInvitationModal
        open={inviteModalOpen}
        onClose={() => setInviteModalOpen(false)}
        onInvitationsSent={handleInvitationsSent}
        onInvitationSendError={handleInvitationSendError}
      />

      <UserEditModal
        open={editModalOpen}
        onClose={handleCloseEditModal}
        user={selectedUser}
      />
    </>
  );
};
export default UsersSettings;
